"use client";

import { useSuspenseQuery } from "@apollo/client";
import {
  Box,
  Group,
  Input,
  type MantineSize,
  Radio,
  RadioGroup,
} from "@mantine/core";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";

import { SelectField } from "@/components/ui/parts";
import { i18n } from "@/i18n";
import {
  JAPAN_NATIONALITY,
  NATIONALITY_TYPE_FOREIGN,
  NATIONALITY_TYPE_JAPAN,
} from "@/types/validation";
import { nationalitiesQuery } from "./_graphql";

type Props<FORM_TYPE extends FieldValues> = {
  control: Control<FORM_TYPE>;
  /** 国籍（国名そのもの）を保持するフォーム項目 */
  name: FieldPath<FORM_TYPE>;
  /** 「日本」か「日本以外」かの二段階選択の1段目を保持するフォーム項目 */
  typeName: FieldPath<FORM_TYPE>;
  disabled?: boolean;
  size?: MantineSize;
};

/**
 * 国籍の入力欄。
 * 「日本」「日本以外」の二段階選択とし、「日本以外」を選んだときだけ国名の一覧を表示する。
 *
 * 1段目の切り替えで2段目の値も書き換えるため、共通の RadioField ではなく
 * Mantine の RadioGroup を直接描画している。
 */
export function NationalityField<FORM_TYPE extends FieldValues>({
  control,
  name,
  typeName,
  disabled,
  size = "sm",
}: Props<FORM_TYPE>) {
  const {
    data: { nationalities },
  } = useSuspenseQuery(nationalitiesQuery, { fetchPolicy: "cache-first" });

  const { field: typeField } = useController({ name: typeName, control });
  const { field: nameField } = useController({ name, control });

  const isForeign = typeField.value === NATIONALITY_TYPE_FOREIGN;

  const handleTypeChange = (value: string) => {
    typeField.onChange(value);
    // 「日本」に戻したときは選択済みの国名を残さない
    nameField.onChange(
      value === NATIONALITY_TYPE_JAPAN ? JAPAN_NATIONALITY : "",
    );
  };

  return (
    <Box>
      <Input.Label required size={size} mb={4}>
        {i18n("nationality")}
      </Input.Label>
      <RadioGroup
        ref={typeField.ref}
        name={typeField.name}
        value={typeField.value ?? NATIONALITY_TYPE_JAPAN}
        onChange={handleTypeChange}
        onBlur={typeField.onBlur}
        size={size}
      >
        <Group>
          <Radio
            value={NATIONALITY_TYPE_JAPAN}
            label="日本"
            color="teal"
            disabled={disabled}
          />
          <Radio
            value={NATIONALITY_TYPE_FOREIGN}
            label="日本以外"
            color="teal"
            disabled={disabled}
          />
        </Group>
      </RadioGroup>
      {isForeign && (
        <SelectField
          mt="xs"
          maw={250}
          name={name}
          control={control}
          data={nationalities}
          placeholder="国名を選択してください"
          searchable
          disabled={disabled}
          size={size}
        />
      )}
    </Box>
  );
}
